import { Button, IconButton } from '@creditornot/cb-button';
import { ArrowLeft } from '@creditornot/cb-icons';
import { Spinner } from '@creditornot/cb-progress';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FORM_ERROR } from 'final-form';
import React, { useState } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import { getInboundMerchant, patchInboundMerchant } from 'apis/merchant-onboarding-service/api';
import { Typography as T } from 'common/typography';

import { getSsmoCountryConfigs } from '../../SelfServeFormView/SelfServeSignUpForm/api';
import CommissionsForm from '../forms/ServicePricingForm/CommissionsForm';
import DevicePreferencesForm from '../forms/ServicePricingForm/DevicePreferencesForm';
import {
  CommissionsPreferences,
  CommissionsPreferencesSchema,
  DevicePreferences,
  DevicePreferencesSchema,
} from '../forms/validation';

type ServicePricingProps = {
  goToNextStep: () => void;
  goToPrevStep: () => void;
};

const Wrapper = styled.div``;

const ContentContainer = styled.div`
  margin-block-end: 32px;
`;

const Title = styled(T.Title1)`
  display: block;
  width: 100%;
  font-weight: 700;
  margin-block-end: 16px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
`;

const StyledButton = styled(Button).attrs({
  size: 'large',
})`
  flex-grow: 1;
`;

const StyledArrowLeft = styled(ArrowLeft)`
  width: 20px;
`;

const BackButton = styled(IconButton).attrs({ variant: 'blue', size: 'small' })`
  border-radius: 12px;
  margin-inline-end: 12px;
  height: 56px;
  width: 56px;

  div {
    border-radius: 8px;
    height: 56px;
    width: 56px;
  }
`;

const ServicePricing = ({ goToNextStep, goToPrevStep }: ServicePricingProps) => {
  const { search } = useLocation();
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  const searchParams = new URLSearchParams(search);
  const merchantId = searchParams.get('merchantId') || '';

  const { isLoading: isInboundMerchandLoading, data: inboundMerchant } = useQuery(
    ['/merchant-admin/inbound-merchants', merchantId],
    () => getInboundMerchant(merchantId),
    {
      refetchOnWindowFocus: false,
    },
  );
  const { isLoading: isSsmoCountryConfigsLoading, data: servicePricingCountryConfig } = useQuery(
    ['/merchant-admin/config/ssmo'],
    getSsmoCountryConfigs,
  );

  const queryClient = useQueryClient();

  const invalidateMerchantInfo = () => queryClient.invalidateQueries(['/merchant-admin/inbound-merchants', merchantId]);

  if (isInboundMerchandLoading || isSsmoCountryConfigsLoading) {
    return <Spinner size="large" />;
  }

  const CommissionsFormId = 'onboarding-wizard-commissions-form';
  const DeviceSelectionFormId = 'onboarding-wizard-device-selection-form';

  const handleMovingOn = () => {
    const hasNextSection = currentSectionIndex < formData.length - 1;

    if (hasNextSection) {
      setCurrentSectionIndex(currentSectionIndex + 1);
    } else {
      goToNextStep();
    }
  };

  const handleMovingBack = () => {
    const hasPrevSection = currentSectionIndex > 0;

    if (hasPrevSection) {
      setCurrentSectionIndex(currentSectionIndex - 1);
    } else {
      goToPrevStep();
    }
  };

  const handleCommissionsSubmit = async (
    values: CommissionsPreferences,
  ): Promise<{ [key: string]: string | Error } | void> => {
    try {
      const validFormValues = CommissionsPreferencesSchema.validateSync(values);

      await patchInboundMerchant(validFormValues, merchantId);
      invalidateMerchantInfo();
      handleMovingOn();
    } catch (submitError) {
      return { [FORM_ERROR]: submitError as Error };
    }
  };

  const handleDevicePreferencesSubmit = async (
    values: DevicePreferences,
  ): Promise<{
    [key: string]: string | Error;
  } | void> => {
    try {
      const validFormValues = DevicePreferencesSchema.validateSync(values);

      await patchInboundMerchant(validFormValues, merchantId);
      invalidateMerchantInfo();
      handleMovingOn();
    } catch (submitError) {
      return { [FORM_ERROR]: submitError as Error };
    }
  };

  const formData = [
    {
      title: 'Commissions in your plan',
      formId: CommissionsFormId,
      formComponent: (
        <CommissionsForm
          commissionConfig={servicePricingCountryConfig!.commissionsConfig}
          commissionsPreferences={{
            joinedToWoltPlus: inboundMerchant!.joinedToWoltPlus || false,
          }}
          handleSubmit={handleCommissionsSubmit}
          formId={CommissionsFormId}
        />
      ),
    },
    {
      title: 'Here’s how you’ll receive your orders',
      formId: DeviceSelectionFormId,
      formComponent: (
        <DevicePreferencesForm
          deviceConfig={servicePricingCountryConfig!.devicesConfig}
          devicePreferences={{
            needsPrinter: inboundMerchant!.needsPrinter || false,
            needsMobileDataPackage: inboundMerchant!.needsMobileDataPackage || false,
          }}
          handleSubmit={handleDevicePreferencesSubmit}
          formId={DeviceSelectionFormId}
        />
      ),
    },
  ];

  const currentRenderContent = formData[currentSectionIndex];

  return (
    <Wrapper>
      {currentRenderContent && (
        <ContentContainer>
          <Title>{currentRenderContent.title}</Title>
          {currentRenderContent.formComponent}
        </ContentContainer>
      )}

      <ButtonsWrapper>
        <BackButton onClick={handleMovingBack}>
          <StyledArrowLeft />
        </BackButton>

        <StyledButton type="submit" form={currentRenderContent.formId} variant="blue">
          Continue
        </StyledButton>
      </ButtonsWrapper>
    </Wrapper>
  );
};

export default ServicePricing;
